import { DateTime } from 'luxon';
import { DurationUnits, DurationObjectUnits, DurationLike } from 'luxon/src/duration';
import { DiffOptions } from 'luxon/src/datetime';
import { DateRange } from 'react-day-picker';
import { DateTimeFormatEnum } from '../../enums';

interface IDate {
  date?: string | null;
  locale?: string | null;
}

export interface IISODateRange {
  from: string | null;
  to: string | null;
}

export const formatDate = (
  params: IDate,
  format: DateTimeFormatEnum = DateTimeFormatEnum.DEFAULT,
) => {
  const { date } = params;
  return (date ? DateTime.fromISO(date).toFormat(format) : null);
};

interface IFormatDateRangesOptions {
  asISODate?: boolean;
  inUTC?: boolean;
}
export const formatDateRanges = (
  range: DateRange,
  options: IFormatDateRangesOptions = {},
): IISODateRange => {
  const { from, to } = range;
  const { asISODate = false, inUTC = true } = options;
  if (!isDateValid(from) || !isDateValid(to)) {
    return { from: null, to: null };
  }
  const parsedFrom = DateTime.fromJSDate(from!);
  const parsedTo = DateTime.fromJSDate(to!);
  if (asISODate) {
    if (inUTC) {
      return { from: parsedFrom.toUTC().toISODate(), to: parsedTo.toUTC().toISODate() };
    }
    return { from: parsedFrom.toISODate(), to: parsedTo.toISODate() };
  }
  return { from: parsedFrom.toUTC().toISO(), to: parsedTo.toUTC().toISO() };
};

export const setStartOfDay = (date: Date): Date => new Date(date.setHours(0, 0, 0, 0));
export const setEndOfDay = (date: Date): Date => new Date(date.setHours(23, 59, 59, 999));

interface IInitDateRangeParams {
  from?: Date | IISODateRange['from'];
  to?: Date | IISODateRange['to'];
  duration?: DurationLike;
}
export const initDateRange = (params: IInitDateRangeParams = {}): DateRange => {
  const { from, to, duration } = params;
  const range: DateRange = { from: undefined, to: undefined };
  if (!from && !to) {
    return range;
  }
  if (from && to) {
    const processedFrom = typeof from === 'string' ? new Date(from) : from;
    const processedTo = typeof to === 'string' ? new Date(to) : to;
    if (!isDateValid(processedFrom) || !isDateValid(processedTo)) {
      return range;
    }
    return { from: setStartOfDay(processedFrom), to: setEndOfDay(processedTo) };
  }
  if (!duration) {
    throw new Error('Provide \'duration\'');
  }
  if (from) {
    const processedFrom = typeof from === 'string' ? new Date(from) : from;
    if (!isDateValid(processedFrom)) {
      return range;
    }
    range.from = processedFrom;
    range.to = DateTime.fromJSDate(processedFrom).plus(duration).toJSDate();
  }
  if (to) {
    const processedTo = typeof to === 'string' ? new Date(to) : to;
    if (!isDateValid(processedTo)) {
      return range;
    }
    range.from = DateTime.fromJSDate(processedTo).minus(duration).toJSDate();
    range.to = processedTo;
  }
  return { from: setStartOfDay(range.from!), to: setEndOfDay(range.to!) };
};

export const getMonthName = (params: IDate) => {
  const { date, locale } = params;
  return (date && locale ? DateTime
    .fromISO(date).toFormat(DateTimeFormatEnum.MONTH, { locale }) : null);
};

export const getTimeFromNow = (params: IDate) => {
  const { date, locale } = params;
  return (date && locale ? DateTime.fromISO(date).toRelative({ locale }) : null);
};

export const getHoursAndMinutes = (time: IDate['date'], format = 'HH:mm') => (
  time ? DateTime.fromISO(time).toFormat(format) : null
);

export const getCurrentTimeZone = (): string => DateTime.local().zone.name;

export const formatDateFromJSDate = (
  date: Date | undefined,
): string => (
  isDateValid(date) ? DateTime.fromJSDate(date as Date).toFormat(DateTimeFormatEnum.DEFAULT) : ''
);

export const isDateBeforeDate = (
  date: Date,
  dateToCompare: Date,
): boolean => (
  DateTime.fromJSDate(date) < DateTime.fromJSDate(dateToCompare)
);

export const isDateValid = (
  date: Date | undefined,
): boolean => (
  date ? DateTime.fromJSDate(date).isValid : false
);

export const isISODateValid = (date: string | undefined): boolean => (
  date ? DateTime.fromISO(date).isValid : false
);

export const getPreviousMonth = (date?: Date): Date => {
  const selectedDate = date ? new Date(date) : new Date();
  return new Date(selectedDate.setDate(0));
};

interface IGetDatesDiffParams {
  unit?: DurationUnits;
  options?: DiffOptions;
}
export const getDatesDiff = (
  range: DateRange,
  params: IGetDatesDiffParams = {},
): DurationObjectUnits => {
  const { unit = 'day', options } = params;
  const { from, to } = range;
  if (!isDateValid(to) || !isDateValid(from)) {
    return {};
  }
  const parsedTo = DateTime.fromJSDate(to!);
  const parsedFrom = DateTime.fromJSDate(from!);
  return parsedTo.diff(parsedFrom, unit, options).toObject();
};

export { type DateRange } from 'react-day-picker';

export const DEFAULT_MIN_DATE = new Date('01/01/2017');
